import * as _ from 'lodash'
import valueFilters from '../valueFilters';
import validator from '../validator';
import modelFactory, { IModel } from '../modelFactory';
import { definition, modelRules } from './jobCostModelExtensions';
import changeOrderModel, { IChangeOrder } from './changeOrderModel';
import workAreaModel, { IJobCostWorkArea } from './jobCostWorkAreaModel';
import jobCostEmployeeModel, { IJobCostEmployee } from './jobCostEmployeeModel';
import installSheetModel, { IInstallSheet } from './installSheetModel';
import jobCostSettingModel, { IJobCostSetting } from './jobCostSettingModel';
import jobCostStatModel, { IJobCostStat } from './jobCostStatModel';
import jobCostSpecificationModel, { IJobCostSpecification } from './jobCostSpecificationModel';
import jobCostLineItemModel, { IJobCostLineItem } from './jobCostLineItemModel';
import jobCostNoteModel, { IJobCostNote } from './jobCostNoteModel';
import jobCostScheduleItemModel, { IJobCostScheduleItem } from './jobCostScheduleItemModel';
import jobCostPaymentTermModel, { IJobCostPaymentTerm } from './jobCostPaymentTermModel';
import proposalModel, { IProposal } from './proposalModel';
import statusLogged, { IStatusLogged } from './statusLogged';
import Specs from './specs';
import model from './model';
import enums from '../enum';
import { IModelSet } from '../modelSet';
import visitable, { IVisitable } from './visitable';
import { getInstallSheets } from 'Scripts/schemaHelper';

export enum JobCostStatus {
	unlocked = 'unlocked',
	locked = 'locked',
	pending_approval = 'pending-approval',
	approved = 'approved',
	rejected = 'rejected',
	order_processing = 'order-processing',
	order_processed = 'order-processed',
	order_delivered = 'order-delivered',
	installed = 'installed',
	inspected = 'inspected'
}

export interface IJobCost extends IModel, IStatusLogged, IVisitable {
	accountingId?: string;
	name?: string;
	description?: string;
	jobKey?: string;
	contractorKey?: string;
	margin?: number;
	templateKey?: string;
	adjustedSellPrice?: string;
	allowInstallSheetChanges?: boolean;
	type?: 'quote' | 'contract' | 'template';
	jobInfoConfirmed?: boolean;
	hasAllSpecs?: boolean;
	installationUnitPrice?: number;
	installationMargin?: number;
	installationMode?: string;
	proposal?: IProposal;
	currentChangeOrder?: IChangeOrder;
	changeOrders: IModelSet<IChangeOrder>;
	employees: IModelSet<IJobCostEmployee>;
	installSheets: IModelSet<IInstallSheet>;
	workAreas: IModelSet<IJobCostWorkArea>;
	scheduleItems: IModelSet<IJobCostScheduleItem>;
	settings: IModelSet<IJobCostSetting>;
	specifications: IModelSet<IJobCostSpecification>;
	stats: IModelSet<IJobCostStat>;
	lineItems: IModelSet<IJobCostLineItem>;
	notes: IModelSet<IJobCostNote>;
	paymentTerms: IModelSet<IJobCostPaymentTerm>;
	cabinetSpecs: Specs;
	workareaSpecs: Specs;
	isContract: boolean;
	allowedContractEdits: 'all' | 'none';
	isSupplyOnly: boolean;
	salesCommissionPercent?: number;
	submittedSummaryData?: object;
	companyVariant?: string;
	builderDiscountPercent: number;
	preorderSubmittedDate?: Date;
	contractProxy?: IJobCost;
	proxiedContract?: IJobCost;
	getEmployeeWithRole(role: string, changeOrderKey?: string): IJobCostEmployee;
	getApprovedChangeOrders(): IChangeOrder[];
	getLineItemsByType(type: string | string[]): IJobCostLineItem[];
	getNote(type: string, relatedKey?: string): IJobCostNote;
	getNoteContent(type: string, relatedKey?: string): string;
	getSetting(group: number, name: string, workAreaKey?: string, autoCreate?: boolean): IJobCostSetting;
	setSetting(group: number, name: string, workAreaKey: string, value?: string): IJobCostSetting;
	getSettingValue(group: number, name: string, workAreaKey: string, defaultValue?: string): string;
	getSettings(group: number, workAreaKey: string);
	getStatValue(name: string, changeOrderKey: string, defaultValue?: number): number;
	withStrictFilters(operation: (jobCost: IJobCost) => void): void;
}

const jobCostFactory = modelFactory.createFactory<IJobCost>('JobCost', _.assign({}, model, visitable, statusLogged(JobCostStatus.unlocked), {
	accountingId: {},
	name: {
		rules: validator.string()
	},
	createdById: {
		rules: validator.string(true)
	},
	description: {
		rules: validator.string(true)
	},
	jobKey: {
		setFilters: valueFilters.clean
	},
	contractorKey: {
		setFilters: valueFilters.clean
	},
	margin: {
		setFilters: valueFilters.cleanNumber,
		getFilters: valueFilters.toNumber,
		rules: validator.greaterThan(0)
	},
	templateKey: {
		setFilters: valueFilters.clean
	},
	adjustedSellPrice: {
		setFilters: valueFilters.cleanNumber,
		getFilters: valueFilters.toNumber
	},
	allowInstallSheetChanges: {
		nonSerialized: true,
		getFilters: val => {
			if (val === '1') {
				return true;
			}
			if (val === '0') {
				return false;
			}
			return undefined;
		}
	},
	// NOTE: (DR) do not use the getter and setter functions, rather use the allowInstallSheetChanges property
	_get_allowInstallSheetChanges: function () {
		const setting = this.getSetting(enums.jobCostSettingGroups.General, 'allowInstallSheetChanges');
		return setting && setting.value;
	},
	_set_allowInstallSheetChanges: function (val) {
		this.setSetting(enums.jobCostSettingGroups.General, 'allowInstallSheetChanges', undefined, val ? '1' : '0');
	},
	type: {
		rules: validator.string(),
		defaultValue: 'quote'
	},
	jobInfoConfirmed: {
		nonSerialized: true,
		setFilters: valueFilters.clean
	},
	// NOTE: (DR) do not use the getter and setter functions, rather use the jobInfoConfirmed property
	_get_jobInfoConfirmed: function () {
		const setting = this.getSetting(enums.jobCostSettingGroups.General, 'jobInfoConfirmed');
		return setting && setting.value;
	},
	_set_jobInfoConfirmed: function (val) {
		this.setSetting(enums.jobCostSettingGroups.General, 'jobInfoConfirmed', undefined, val);
	},
	hasAllSpecs: {
		nonSerialized: true,
		setFilters: valueFilters.clean
	},
	// NOTE: (DR) do not use the getter and setter functions, rather use the hasAllSpecs property
	_get_hasAllSpecs: function () {
		const setting = this.getSetting(enums.jobCostSettingGroups.General, 'hasAllSpecs');
		return setting && setting.value;
	},
	_set_hasAllSpecs: function (val) {
		this.setSetting(enums.jobCostSettingGroups.General, 'hasAllSpecs', undefined, val);
	},
	installationUnitPrice: {
		nonSerialized: true,
		setFilters: valueFilters.cleanNumber,
		getFilters: valueFilters.toNumber,
		rules: validator.number(true) // allow undefined
	},
	// NOTE: (DR) do not use the getter and setter functions, rather use the installationUnitPrice property
	_get_installationUnitPrice: function () {
		const setting = this.getSetting(enums.jobCostSettingGroups.General, 'installationUnitPrice');
		return setting && setting.value;
	},
	_set_installationUnitPrice: function (val) {
		this.setSetting(enums.jobCostSettingGroups.General, 'installationUnitPrice', undefined, val);
	},
	installationMargin: {
		nonSerialized: true,
		setFilters: valueFilters.cleanNumber,
		getFilters: valueFilters.toNumber,
		rules: validator.number(true) // allow undefined
	},
	// NOTE: (DR) do not use the getter and setter functions, rather use the installationMargin property
	_get_installationMargin: function () {
		const setting = this.getSetting(enums.jobCostSettingGroups.General, 'installationMargin');
		return setting && setting.value;
	},
	_set_installationMargin: function (val) {
		this.setSetting(enums.jobCostSettingGroups.General, 'installationMargin', undefined, val);
	},
	installationMode: {
		nonSerialized: true,
		setFilters: valueFilters.clean
	},
	// NOTE: (DR) do not use the getter and setter functions, rather use the installationMode property
	_get_installationMode: function () {
		const setting = this.getSetting(enums.jobCostSettingGroups.General, 'installationMode');
		return setting && setting.value;
	},
	_set_installationMode: function (val) {
		this.setSetting(enums.jobCostSettingGroups.General, 'installationMode', undefined, val);
	},
	proposal: {
		nonSerialized: true
	},
	// NOTE: (DR) do not use the getter and setter functions, rather use the proposal property
	_get_proposal: function () {
		const innerVal = this.__getBackingValue('proposal');

		if (!innerVal.hasBeenSet) {
			const setting = this.getSetting(enums.jobCostSettingGroups.General, 'proposalData');

			if (setting && setting.value) {
				innerVal.value = proposalModel(JSON.parse(setting.value));
				innerVal.hasBeenSet = !!innerVal.value;
			}
		}

		return innerVal.value;
	},
	_set_proposal: function (val) {
		const innerVal = this.__getBackingValue('proposal');

		innerVal.value = val;
		innerVal.hasBeenSet = !!val;

		this.setSetting(enums.jobCostSettingGroups.General, 'proposalData', undefined, val && JSON.stringify(val.toDocument()));
	},
	changeOrders: {
		setFilters: valueFilters.toModelSet(changeOrderModel),
		rules: validator.validModel(changeOrderModel.rules()),
		defaultValue: []
	},
	employees: {
		setFilters: valueFilters.toModelSet(jobCostEmployeeModel),
		rules: validator.validModel(jobCostEmployeeModel.rules()),
		defaultValue: []
	},
	installSheets: {
		setFilters: valueFilters.toModelSet(installSheetModel),
		rules: validator.validModel(installSheetModel.rules()),
		defaultValue: []
	},
	workAreas: {
		setFilters: valueFilters.toModelSet(workAreaModel),
		rules: validator.validModel(workAreaModel.rules()),
		defaultValue: []
	},
	scheduleItems: {
		setFilters: valueFilters.toModelSet(jobCostScheduleItemModel),
		rules: validator.validModel(jobCostScheduleItemModel.rules()),
		defaultValue: []
	},
	settings: {
		setFilters: valueFilters.toModelSet(jobCostSettingModel),
		rules: validator.validModel(jobCostSettingModel.rules()),
		defaultValue: []
	},
	specifications: {
		setFilters: valueFilters.toModelSet(jobCostSpecificationModel),
		rules: validator.validModel(jobCostSpecificationModel.rules()),
		defaultValue: []
	},
	stats: {
		setFilters: valueFilters.toModelSet(jobCostStatModel),
		rules: validator.validModel(jobCostStatModel.rules()),
		defaultValue: []
	},
	lineItems: {
		setFilters: valueFilters.toModelSet(jobCostLineItemModel),
		rules: validator.validModel(jobCostLineItemModel.rules()),
		defaultValue: []
	},
	notes: {
		setFilters: valueFilters.toModelSet(jobCostNoteModel),
		rules: validator.validModel(jobCostNoteModel.rules()),
		defaultValue: []
	},
	paymentTerms: {
		setFilters: valueFilters.toModelSet(jobCostPaymentTermModel),
		rules: validator.validModel(jobCostPaymentTermModel.rules()),
		defaultValue: []
	},
	cabinetSpecs: {
		writable: false,
		nonSerialized: true,
		defaultValue: function () { return new Specs(this, 'cabinetSpecDefinition', enums.specGroups.Cabinet); }
	},
	workAreaSpecs: {
		writable: false,
		nonSerialized: true,
		defaultValue: function () { return new Specs(this, 'workAreaSpecDefinition', enums.specGroups.Work_Area); }
	},
	isContract: {
		writable: false,
		nonSerialized: true
	},
	_get_isContract: function () {
		return !!(this.type === 'contract' || this.getMostRecentStatusByStatus('pending-approval'));
	},
	allowedContractEdits: {
		writable: false,
		nonSerialized: true
	},
	_get_allowedContractEdits: function () {
		return (['unlocked', 'rejected'].indexOf(this.currentStatus) !== -1) ? 'all' : 'none';
	}, 
	isSupplyOnly: {
		writable: false,
		nonSerialized: true
	},
	_get_isSupplyOnly: function() {
		const value = this.getSetting(enums.jobCostSettingGroups.General, 'supplyOnly') ? !!Number(this.getSettingValue(enums.jobCostSettingGroups.General, 'supplyOnly')) : this.getLineItemsByType(['installation', 'installation-custom']).length === 0;
		return value;
	},
	salesCommissionPercent: {
		nonSerialized: true,
		setFilters: valueFilters.cleanNumber,
		getFilters: valueFilters.toNumber,
		rules: validator.number(true) // allow undefined
	},
	// NOTE: (DR) do not use the getter and setter functions, rather use the salesCommissionPercent property
	_get_salesCommissionPercent: function () {
		const setting = this.getSetting(enums.jobCostSettingGroups.General, 'salesCommissionPercent');
		return setting && setting.value;
	},
	_set_salesCommissionPercent: function (val) {
		this.setSetting(enums.jobCostSettingGroups.General, 'salesCommissionPercent', undefined, val);
	},
	submittedSummaryData: {
		nonSerialized: true
	},
	// NOTE: (DR) do not use the getter and setter functions, rather use the submittedSummaryData property
	_get_submittedSummaryData: function () {
		const setting = this.getSetting(enums.jobCostSettingGroups.General, 'submittedSummaryData');
		return setting && setting.value && JSON.parse(setting.value);
	},
	_set_submittedSummaryData: function (val) {
		this.setSetting(enums.jobCostSettingGroups.General, 'submittedSummaryData', undefined, val && JSON.stringify(val));
	},
	companyVariant: {
		nonSerialized: true
	},
	// NOTE: (DR) do not use the getter and setter functions, rather use the companyVariant property
	_get_companyVariant: function () {
		const setting = this.getSetting(enums.jobCostSettingGroups.General, 'companyVariant');
		return setting && setting.value;
	},
	_set_companyVariant: function (val) {
		this.setSetting(enums.jobCostSettingGroups.General, 'companyVariant', undefined, val);
	},
	builderDiscountPercent: {
		nonSerialized: true
	},
	// NOTE: (DR) do not use the getter and setter functions, rather use the builderDiscountPercent property
	_get_builderDiscountPercent: function () {
		const setting = this.getSetting(enums.jobCostSettingGroups.General, 'builderDiscountPercent');
		return (setting && setting.value) || 0;
	},
	_set_builderDiscountPercent: function () {
		throw new Error('jobCost.builderDiscountPercent cannot be set directly');
	},
	preorderSubmittedDate: {
		nonSerialized: true
	},
	// NOTE: (DR) do not use the getter and setter functions, rather use the preorderSubmittedDate property
	_get_preorderSubmittedDate: function () {
		const key = this.currentChangeOrder ? `preorderSubmittedDate-co-${this.currentChangeOrder.key}` : 'preorderSubmittedDate';
		const setting = this.getSetting(enums.jobCostSettingGroups.General, key);

		return (setting && new Date(setting.value)) || undefined;
	},
	_set_preorderSubmittedDate: function (val) {
		const key = this.currentChangeOrder ? `preorderSubmittedDate-co-${this.currentChangeOrder.key}` : 'preorderSubmittedDate';

		this.setSetting(enums.jobCostSettingGroups.General, key, undefined, val && val.toISOString());
	},
	getEmployeeWithRole: function (role, changeOrderKey) {
		if (!changeOrderKey) {
			return _.find(this.employees, { role: role });
		}
		else {
			return _.find(this.employees, { role: role, changeOrderKey: changeOrderKey });
		}
	},
	getApprovedChangeOrders: function () {
		return _.filter(this.changeOrders, co => co.isContract);
	},
	getLineItemsByType: function (types) {
		if (!_.isArray(types)) {
			types = types === '*' ? jobCostLineItemModel.TYPES : [types];
		}
		return _.filter(this.lineItems, item => { return types.indexOf(item.type) !== -1; });
	},
	getNote: function (type, relatedKey) {
		return _.find(this.notes, { type: type, relatedKey: relatedKey });
	},
	getNoteContent: function (type, relatedKey) {
		const note = this.getNote(type, relatedKey);

		return note ? note.content : '';
	},
	getSetting: function (group, name, workAreaKey, autoCreate = false) {
		if (!this.settings) { return; }

		let result = _.find(this.settings, { group: group, name: name, workAreaKey: workAreaKey });
		if (!result && autoCreate) {
			result = this.settings.add({
				name: name,
				group: group,
				workAreaKey: workAreaKey
			});
		}
		return result;
	},
	setSetting: function (group, name, workAreaKey, value) {
		if (!this.settings) { return; }

		const isUndefined = typeof value === 'undefined';
		let result = this.getSetting(group, name, workAreaKey, !isUndefined);

		if (result) {
			if (isUndefined) {
				this.settings.delete(result.key);
				result = undefined;
			}
			else {
				result.value = value;
			}
		}

		return result;
	},
	getSettingValue: function (group, name, workAreaKey, defaultValue = '') {
		const setting = this.getSetting(group, name, workAreaKey);

		return setting && setting.value || defaultValue;
	},
	getSettings: function (group, workAreaKey) {
		return _.filter(this.settings, { group: group, workAreaKey: workAreaKey });
	},
	getStatValue(name, changeOrderKey, defaultValue) {
		const group = enums.jobCostStatGroups.Contract_Summary;
		const stat = _.find(this.stats, s => {
			return s.group === group && s.name === name && s.changeOrderKey === changeOrderKey;
		});

		return stat ? Number(stat.value) : defaultValue;
	},
	withStrictFilters: function (operation) {
		// this is here for contractProxy compatibility
		return operation(this);
	},
	createLineItem: function (data) {
		return jobCostLineItemModel(_.assign(data, { changeOrderKey: this.currentChangeOrder && this.currentChangeOrder.key }));
	},
	createWorkArea: function (data) {
		const rootName = data.name && data.name.replace(/\s\d+$/, '');
		let nameSuffix = 1;
		let newName = rootName;

		// ensure unique work area name
		if (rootName) {
			while (_.find(this.workAreas, area => area.name.toLowerCase() === newName.toLowerCase())) { //eslint-disable-line  no-loop-func
				newName = `${rootName} ${nameSuffix++}`;
			}

			data.name = newName;
		}

		data.name = valueFilters.trim(data.name);

		const sortIndex = _.reduce(this.workAreas, (max, w) => w.sortIndex >= max ? w.sortIndex + 1 : max, 0);

		return workAreaModel(_.assign(data, { sortIndex }));
	},
	copyWorkArea: function (workAreaKey, newWorkAreaName, destinationWorkAreas) {
		const sourceWorkArea = this.workAreas.get(workAreaKey);

		if (!sourceWorkArea) { return; }

		if (newWorkAreaName) {
			destinationWorkAreas.push(this.workAreas.add(this.createWorkArea({ name: newWorkAreaName })));
		}

		const customSpecs = _.filter(this.specifications, { group: enums.specGroups.Work_Area_Custom, workAreaKey: workAreaKey });

		// Copy the vendor key first so the specifications get added
		_.forEach(destinationWorkAreas, workArea => {
			// console.log('Work Area: ', f.workArea);
			workArea.vendorKey = sourceWorkArea.vendorKey;
			this.workAreaSpecs.clone(sourceWorkArea.key, workArea.key);
			this.cabinetSpecs.clone(sourceWorkArea.key, workArea.key);

			_.forEach(customSpecs, sourceSpec => {
				const def = { group: sourceSpec.group, workAreaKey: workArea.key, name: sourceSpec.name };
				let targetSpec = _.find(this.specifications, def);

				if (this.currentChangeOrder || sourceSpec.value) {
					// All changes in a change order require a new spec, if the value is equal to the parent, setting the value below will remove it
					if (!targetSpec) {
						targetSpec = this.specifications.add(def);
					}
				}
				else if (targetSpec) {
					this._jobCost.specifications.delete(targetSpec.key);
					targetSpec = undefined;
				}

				if (targetSpec) {
					targetSpec.value = sourceSpec.value;
				}
			});
		});

		// this.sortWorkAreas();
	},
	sortWorkAreas() {
		// for now, keep sortIndex in sync with alphabetical name sort
		let sortIndex = 0;
		_.forEach(_.sortBy(this.workAreas, 'name'), wa => {
			wa.sortIndex = sortIndex++;
		});
	},
	getVendorOverheadFactor: function (vendorKey) {
		const setting = this.getSetting(enums.jobCostSettingGroups.General, 'vendorOverheadFactors');
		const parsed = setting && JSON.parse(setting.value);

		return parsed ? Number(parsed[vendorKey] || '0') : 0;
	},
	setVendorOverheadFactor: function (vendorKey, factor) {
		const setting = this.getSetting(enums.jobCostSettingGroups.General, 'vendorOverheadFactors', undefined, true);
		const val = setting.value || '{}';
		const parsed = JSON.parse(val);

		parsed[vendorKey] = factor;

		setting.value = JSON.stringify(parsed);
	},
	// NOTE: job costs with change orders should not be cloned!
	clone: function (context, workAreaKeysToClone) {
		const workAreaKeyMap = {};
		const installSheetKeyMap = {};

		if (this.changeOrders.length()) {
			throw new Error('Jobcosts cannot be copied if they contain change orders.');
		}

		/* eslint-disable indent */
		const filter = function (ctx) {
			switch (ctx.property) {
				case 'workAreas':
					if (ctx.source.workAreas) {
						const workAreasToClone = workAreaKeysToClone ? _.filter(ctx.source.workAreas, workArea => workAreaKeysToClone.indexOf(workArea.key) !== -1) : ctx.source.workAreas;
						ctx.target.workAreas = _.map(workAreasToClone, workArea => {
							const workAreaClone = workArea.clone(ctx);
							workAreaKeyMap[workArea.key] = workAreaClone.key;
							return workAreaClone;
						});
					}
					return false;
				case 'installSheets':
					if (getInstallSheets(ctx.source)) {
						ctx.target.installSheets = _.map(getInstallSheets(ctx.source), installSheet => {
							const clone = installSheet.clone(ctx);
							installSheetKeyMap[installSheet.key] = clone.key;
							return clone;
						});
					}
					return false;
				case 'relayId':
				case 'cabinetSpecs':
				case 'workAreaSpecs':
				case 'settings':
				case 'specifications':
				case 'lineItems':
				case 'statusLog':
				case 'scheduleItems':
				case 'paymentTerms':
				case 'stats':
					return false; // exclude these from the result
				default:
					break;
			}

			return true;
		};
		/* eslint-enable indent */

		const clone = this._clone(context, filter);

		clone.type = clone.type === 'contract' ? 'quote' : clone.type;

		// This cloning is delayed so that we can use mapped work area keys
		_.forEach(['settings', 'specifications', 'lineItems'], k => {
			if (this[k]) {
				const itemsToClone = workAreaKeysToClone ? _.filter(this[k], item => !item.workAreaKey || workAreaKeysToClone.indexOf(item.workAreaKey) !== -1) : this[k];
				clone[k] = _.map(itemsToClone, item => {
					const itemClone = item.clone();
					if (itemClone.workAreaKey) {
						itemClone.workAreaKey = workAreaKeyMap[itemClone.workAreaKey];
					}
					if (itemClone.installSheetKey) {
						itemClone.installSheetKey = installSheetKeyMap[itemClone.installSheetKey];
					}
					return itemClone;
				});
			}
		});

		_.forEach(_.filter(clone.lineItems, item => ['calculated-fee', 'warranty', 'tax'].indexOf(item.type) !== -1), item => {
			clone.lineItems.delete(item.key);
		});

		clone.proposal = undefined; // clear the proposal if it exists

		return clone;
	}
}, definition || {}), modelRules || []); 

export default jobCostFactory;